// ConfigFacturacion.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  ShieldCheck,
  ArrowLeft,
  Save,
  RefreshCcw,
  Plus,
  Pencil,
  X,
  Check,
  Trash2,
  Eye,
  EyeOff,
  Hash,
  Target,
  Activity,
  KeyRound,
  Upload,
  Eraser
} from 'lucide-react';
import AfipCertificateWizard from '../components/AfipCertificateWizard';

/**
 * Configuración de Facturación (por Emisor / Multi-CUIT)
 * - SIN configuración general: todo se configura por cada emisor (billing_entities).
 * - Un solo "Tipo de Responsable" en UI: se guarda en tax_condition y fiscal_configuration (compatibilidad).
 * - Credenciales por emisor (4 secretos):
 *   - cert_homo, key_homo, cert_prod, key_prod
 *
 * Nota: Sin modales. Edición inline embebida. Layout estilo Productos.
 */

const AFIP_MODES = [
  { value: 'homologacion', label: 'Homologación' },
  { value: 'produccion', label: 'Producción' }
];

// Ajustá/extendé este catálogo cuando quieras
const FISCAL_OPTIONS = [
  { value: 'Responsable Inscripto', label: 'Responsable Inscripto' },
  { value: 'Monotributo', label: 'Monotributista' },
  { value: 'Exento', label: 'Exento' },
  { value: 'Consumidor Final', label: 'Consumidor Final' }
];

const clampMonthKey = (s) => {
  const v = String(s || '').trim();
  if (!/^\d{4}-\d{2}$/.test(v)) return '';
  return v;
};

const buildMonthKeyNow = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${m}`;
};

const normalizeCuit = (c) => String(c || '').replace(/\D/g, '').slice(0, 11);

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error || new Error('No se pudo leer el archivo'));
      reader.readAsText(file);
    } catch (e) {
      reject(e);
    }
  });

// “Normalización” suave: no toca contenido PEM, solo recorta extremos.
// (evita que se te cuele un espacio o salto de línea al principio/fin)
const normalizeSecretText = (txt) => String(txt || '').replace(/\u0000/g, '').trim();

export default function ConfigFacturacion({ onNavigate } = {}) {
  const navigate = useNavigate();
  const { profile, organization } = useAuth();

  const [loading, setLoading] = useState(true);

  // Org (solo para header/contexto) — viene del AuthContext.
  const org = organization;

  // Emisores
  const [billingEntities, setBillingEntities] = useState([]);

  // Credenciales por emisor (tabla separada)
  // Map: billing_entity_id -> { cert_homo, key_homo, cert_prod, key_prod }
  const [credsByBE, setCredsByBE] = useState({});
  const [credsAvailable, setCredsAvailable] = useState(true); // si tabla no existe o falla, no rompemos

  // UI
  const [editingId, setEditingId] = useState(null);
  const [beDraft, setBeDraft] = useState(null);
  const [certificateWizardOpen, setCertificateWizardOpen] = useState(false);

  // límites mensuales
  const [limitMonth, setLimitMonth] = useState(buildMonthKeyNow());
  const [limitAmount, setLimitAmount] = useState('');

  // secretos (draft)
  const [secretDraft, setSecretDraft] = useState({
    cert_homo: '',
    key_homo: '',
    cert_prod: '',
    key_prod: ''
  });

  // nombre de archivos cargados (solo UI)
  const [secretFiles, setSecretFiles] = useState({
    cert_homo: '',
    key_homo: '',
    cert_prod: '',
    key_prod: ''
  });

  const [showSecrets, setShowSecrets] = useState({
    cert_homo: false,
    key_homo: false,
    cert_prod: false,
    key_prod: false
  });
  const [focusSection, setFocusSection] = useState('');
  const generalSectionRef = useRef(null);
  const certificatesSectionRef = useRef(null);
  const limitsSectionRef = useRef(null);

  // filtros
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);

  const back = () => {
    if (typeof onNavigate === 'function') return onNavigate('dashboard');
    navigate(-1);
  };

  // toggleOrgPostSaleActions removed as posDefaultAction is now in ConfigReportes

  const fetchEntities = async (orgId) => {
    const { data, error } = await supabase
      .from('billing_entities')
      .select(
        `
        id,
        organization_id,
        tax_name,
        cuit,
        tax_condition,
        fiscal_configuration,
        afip_pto_venta,
        iibb,
        inicio_actividades,
        domicilio_comercial,
        is_active,
        created_at,
        afip_mode,
        monthly_limits,
        auto_invoice,
        is_blocking,
        priority
      `
      )
      .eq('organization_id', orgId)
      .order('priority', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) throw error;
    setBillingEntities(Array.isArray(data) ? data : []);
  };

  const fetchCredentials = async (orgId, entityIds) => {
    // Tabla opcional: si no existe, no frenamos
    try {
      if (!entityIds?.length) {
        setCredsByBE({});
        setCredsAvailable(true);
        return;
      }

      const { data, error } = await supabase
        .from('billing_entity_credentials')
        .select('billing_entity_id, cert_homo, key_homo, cert_prod, key_prod')
        .eq('organization_id', orgId)
        .in('billing_entity_id', entityIds);

      if (error) throw error;

      const map = {};
      (data || []).forEach((r) => {
        map[r.billing_entity_id] = {
          cert_homo: r.cert_homo || '',
          key_homo: r.key_homo || '',
          cert_prod: r.cert_prod || '',
          key_prod: r.key_prod || ''
        };
      });

      setCredsByBE(map);
      setCredsAvailable(true);
    } catch (e) {
      console.warn('billing_entity_credentials no disponible o sin permisos:', e?.message || e);
      setCredsAvailable(false);
      setCredsByBE({});
    }
  };

  const fetchAll = async () => {
    if (!profile?.organization_id) return;
    setLoading(true);
    try {
      const orgId = profile.organization_id;
      // org ya viene cargado desde el AuthContext; solo cargamos las entidades.
      await fetchEntities(orgId);

      const ids = (billingEntities || []).map((b) => b.id);
      // Ojo: billingEntities todavía no se actualizó en estado en este tick.
      // Solución: volvemos a consultar ids desde la query directa:
      const { data: beIds, error: beIdsErr } = await supabase
        .from('billing_entities')
        .select('id')
        .eq('organization_id', orgId);

      if (beIdsErr) throw beIdsErr;
      const entityIds = (beIds || []).map((x) => x.id);

      await fetchCredentials(orgId, entityIds);
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron cargar las configuraciones de facturación.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  const activeCount = useMemo(() => billingEntities.filter((b) => b?.is_active).length, [billingEntities]);

  const filtered = useMemo(() => {
    const q = String(search || '').toLowerCase().trim();
    const base = billingEntities || [];
    return base.filter((b) => {
      if (onlyActive && !b?.is_active) return false;
      if (!q) return true;
      const hay = `${b?.tax_name || ''} ${b?.cuit || ''} ${b?.fiscal_configuration || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [billingEntities, search, onlyActive]);

  const resetEditor = () => {
    setEditingId(null);
    setBeDraft(null);
    setCertificateWizardOpen(false);
    setFocusSection('');
    setLimitMonth(buildMonthKeyNow());
    setLimitAmount('');
    setSecretDraft({ cert_homo: '', key_homo: '', cert_prod: '', key_prod: '' });
    setSecretFiles({ cert_homo: '', key_homo: '', cert_prod: '', key_prod: '' });
    setShowSecrets({ cert_homo: false, key_homo: false, cert_prod: false, key_prod: false });
  };

  const startNew = (section = 'general') => {
    setEditingId('__new__');
    setCertificateWizardOpen(false);
    setFocusSection(section);
    const defaultFiscal = 'Responsable Inscripto';
    setBeDraft({
      id: '__new__',
      organization_id: profile.organization_id,
      tax_name: '',
      cuit: '',
      fiscal_configuration: defaultFiscal,
      tax_condition: defaultFiscal, // compat
      afip_pto_venta: 1,
      afip_mode: 'homologacion',
      is_active: true,
      auto_invoice: false,
      is_blocking: true,
      priority: 1,
      monthly_limits: { fiscal_meta: {} }
    });
    setSecretDraft({ cert_homo: '', key_homo: '', cert_prod: '', key_prod: '' });
    setSecretFiles({ cert_homo: '', key_homo: '', cert_prod: '', key_prod: '' });
    setLimitMonth(buildMonthKeyNow());
    setLimitAmount('');
  };

  const startEdit = (row, section = 'general') => {
    setEditingId(row.id);
    setCertificateWizardOpen(false);
    setFocusSection(section);

    const fiscal = row.fiscal_configuration || row.tax_condition || 'Responsable Inscripto';

    setBeDraft({
      ...row,
      fiscal_configuration: fiscal,
      tax_condition: fiscal, // compat
      monthly_limits: { ...(row?.monthly_limits || {}), fiscal_meta: (row?.monthly_limits?.fiscal_meta || {}) }
    });

    const creds = credsByBE?.[row.id] || { cert_homo: '', key_homo: '', cert_prod: '', key_prod: '' };
    setSecretDraft({
      cert_homo: creds.cert_homo || '',
      key_homo: creds.key_homo || '',
      cert_prod: creds.cert_prod || '',
      key_prod: creds.key_prod || ''
    });

    // Si vienen desde DB, no asumimos nombre de archivo.
    setSecretFiles({ cert_homo: '', key_homo: '', cert_prod: '', key_prod: '' });

    setLimitMonth(buildMonthKeyNow());
    setLimitAmount('');
  };

  const jumpToSection = (section) => {
    setFocusSection(section);
  };

  useEffect(() => {
    if (!editingId || !beDraft || certificateWizardOpen || !focusSection) return;

    const sectionMap = {
      general: generalSectionRef,
      certificates: certificatesSectionRef,
      limits: limitsSectionRef
    };
    const node = sectionMap[focusSection]?.current;
    if (!node) return;

    const frame = window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [editingId, beDraft, certificateWizardOpen, focusSection]);

  const updateDraft = (k, v) => setBeDraft((prev) => ({ ...(prev || {}), [k]: v }));

  // Datos fiscales por CUIT (para impresión / PDF): se guardan dentro de billing_entities.monthly_limits.fiscal_meta
  // (Elegimos este lugar porque monthly_limits existe en DB y es jsonb; evita migraciones y no rompe compatibilidad.)
  const getFiscalMeta = (row) => {
    try {
      return (row?.monthly_limits && row.monthly_limits.fiscal_meta) ? row.monthly_limits.fiscal_meta : {};
    } catch {
      return {};
    }
  };

  const updateFiscalMeta = (k, v) => {
    setBeDraft((prev) => {
      const next = { ...(prev || {}) };
      const ml = { ...(next.monthly_limits || {}) };
      const fm = { ...(ml.fiscal_meta || {}) };
      fm[k] = v;
      ml.fiscal_meta = fm;
      next.monthly_limits = ml;
      return next;
    });
  };

  const setMonthlyLimit = () => {
    if (!beDraft) return;
    const mk = clampMonthKey(limitMonth);
    if (!mk) return toast.error('Mes inválido. Formato: YYYY-MM');

    const amt = Number(String(limitAmount || '').replace(',', '.'));
    if (!Number.isFinite(amt) || amt < 0) return toast.error('Monto inválido.');

    setBeDraft((prev) => {
      const next = { ...(prev || {}) };
      const ml = { ...(next.monthly_limits || {}) };
      ml[mk] = amt;
      next.monthly_limits = ml;
      return next;
    });

    setLimitAmount('');
    toast.success('Límite mensual actualizado en borrador (recordá guardar).');
  };

  const deleteMonthlyLimit = (mk) => {
    if (!beDraft) return;
    setBeDraft((prev) => {
      const next = { ...(prev || {}) };
      const ml = { ...(next.monthly_limits || {}) };
      delete ml[mk];
      next.monthly_limits = ml;
      return next;
    });
    toast.success('Límite eliminado en borrador (recordá guardar).');
  };

  const monthlyLimitsPairs = useMemo(() => {
    const ml = beDraft?.monthly_limits || {};
    return Object.entries(ml)
      .filter(([mk]) => /^\d{4}-\d{2}$/.test(String(mk || '')))
      .sort((a, b) => String(b[0]).localeCompare(String(a[0])));
  }, [beDraft?.monthly_limits]);

  const getRowMonthLimit = (row, mk = buildMonthKeyNow()) => {
    const monthlyLimits = row?.monthly_limits && typeof row.monthly_limits === 'object' ? row.monthly_limits : {};
    const raw = monthlyLimits?.[mk];
    const amount = Number(raw || 0);
    return Number.isFinite(amount) && amount > 0 ? amount : 0;
  };

  const getRowLimitsCount = (row) => {
    const monthlyLimits = row?.monthly_limits && typeof row.monthly_limits === 'object' ? row.monthly_limits : {};
    return Object.keys(monthlyLimits).filter((mk) => /^\d{4}-\d{2}$/.test(String(mk || ''))).length;
  };

  const toggleShowSecret = (k) => setShowSecrets((prev) => ({ ...prev, [k]: !prev[k] }));

  const updateSecret = (k, v) => {
    setSecretDraft((prev) => ({ ...prev, [k]: v }));
    // si el usuario escribe manual, “desacoplamos” nombre de archivo para evitar confusión
    setSecretFiles((prev) => ({ ...prev, [k]: prev[k] }));
  };

  const clearSecret = (k) => {
    setSecretDraft((prev) => ({ ...prev, [k]: '' }));
    setSecretFiles((prev) => ({ ...prev, [k]: '' }));
    toast.success('Campo limpiado.');
  };

  const handleSecretFile = async (k, file) => {
    if (!file) return;
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > 2) {
      toast.error('El archivo es muy grande (máx 2MB).');
      return;
    }

    const toastId = toast.loading(`Leyendo ${file.name}...`);
    try {
      const txt = await readFileAsText(file);
      const normalized = normalizeSecretText(txt);

      if (!normalized) {
        toast.error('El archivo está vacío o no se pudo interpretar.', { id: toastId });
        return;
      }

      // Validación estricta para PEM
      const isCert = k.includes('cert');
      const isKey = k.includes('key');
      let isValidPem = false;
      
      if (isCert) {
        isValidPem = normalized.includes('BEGIN CERTIFICATE') && normalized.includes('END CERTIFICATE');
      } else if (isKey) {
        isValidPem = (normalized.includes('BEGIN PRIVATE KEY') || normalized.includes('BEGIN RSA PRIVATE KEY')) && 
                     (normalized.includes('END PRIVATE KEY') || normalized.includes('END RSA PRIVATE KEY'));
      }

      if (!isValidPem && file.name.match(/\.(crt|key|pem|cer)$/i)) {
        toast.error(`El archivo no parece ser un ${isCert ? 'Certificado' : 'Key'} PEM válido. Faltan las cabeceras BEGIN/END.`, { id: toastId });
        return;
      }
      
      const hint = isValidPem ? 'PEM detectado' : 'Texto cargado';

      setSecretDraft((prev) => ({ ...prev, [k]: normalized }));
      setSecretFiles((prev) => ({ ...prev, [k]: file.name }));
      setShowSecrets((prev) => ({ ...prev, [k]: true })); // UX: si cargó archivo, que lo pueda ver sin pelearse

      toast.success(`Archivo cargado (${hint}).`, { id: toastId });
    } catch (e) {
      console.error(e);
      toast.error('No se pudo leer el archivo.', { id: toastId });
    }
  };

  const hasAnySecret = useMemo(() => {
    return Boolean(
      (secretDraft.cert_homo || '').trim() ||
        (secretDraft.key_homo || '').trim() ||
        (secretDraft.cert_prod || '').trim() ||
        (secretDraft.key_prod || '').trim()
    );
  }, [secretDraft]);

  const saveDraft = async () => {
    if (!beDraft || !profile?.organization_id) return;

    const orgId = profile.organization_id;
    const isNew = beDraft.id === '__new__';

    const fiscal = String(beDraft.fiscal_configuration || '').trim();
    const payload = {
      organization_id: orgId,
      tax_name: String(beDraft.tax_name || '').trim(),
      cuit: normalizeCuit(beDraft.cuit),
      // Unificamos fiscal en ambos campos para compatibilidad y evitar doble verdad
      fiscal_configuration: fiscal,
      tax_condition: fiscal,
      afip_pto_venta: Number(beDraft.afip_pto_venta) || 1,
      // Datos que el comprobante impreso exige (RG 1415). Opcionales: vacío = null.
      iibb: String(beDraft.iibb || '').trim() || null,
      inicio_actividades: beDraft.inicio_actividades || null,
      domicilio_comercial: String(beDraft.domicilio_comercial || '').trim() || null,
      afip_mode: String(beDraft.afip_mode || 'homologacion'),
      is_active: Boolean(beDraft.is_active),
      is_blocking: Boolean(beDraft.is_blocking ?? true),
      auto_invoice: Boolean(beDraft.auto_invoice ?? false),
      priority: Number(beDraft.priority ?? 1),
      monthly_limits: beDraft.monthly_limits || {}
    };

    if (!payload.tax_name) return toast.error('El nombre fiscal es obligatorio.');
    if (!payload.cuit || payload.cuit.length !== 11) return toast.error('CUIT inválido (debe tener 11 dígitos).');
    if (!payload.fiscal_configuration) return toast.error('Seleccioná el tipo de responsable.');

    try {
      let savedEntityId = null;

      if (isNew) {
        const { data, error } = await supabase.from('billing_entities').insert(payload).select('id').maybeSingle();
        if (error) throw error;
        savedEntityId = data?.id || null;
        toast.success('Emisor creado.');
      } else {
        const { error } = await supabase.from('billing_entities').update(payload).eq('id', beDraft.id);
        if (error) throw error;
        savedEntityId = beDraft.id;
        toast.success('Emisor actualizado.');
      }

      // Guardar credenciales en tabla separada (si existe)
      if (credsAvailable && savedEntityId) {
        const credPayload = {
          organization_id: orgId,
          billing_entity_id: savedEntityId,
          cert_homo: secretDraft.cert_homo || '',
          key_homo: secretDraft.key_homo || '',
          cert_prod: secretDraft.cert_prod || '',
          key_prod: secretDraft.key_prod || ''
        };

        // Upsert por billing_entity_id (unique)
        const { error: credErr } = await supabase
          .from('billing_entity_credentials')
          .upsert(credPayload, { onConflict: 'billing_entity_id' });

        if (credErr) {
          console.warn(credErr);
          toast.error('Emisor guardado, pero no se pudieron guardar las credenciales (revisá tabla/permisos).');
        } else {
          toast.success('Credenciales guardadas.');
        }
      }

      resetEditor();
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo guardar el emisor.');
    }
  };

  const removeEntity = async (id) => {
    if (!id) return;
    const ok = window.confirm('¿Eliminar emisor? Esta acción no se puede deshacer.');
    if (!ok) return;

    try {
      const { error } = await supabase.from('billing_entities').delete().eq('id', id);
      if (error) throw error;
      toast.success('Emisor eliminado.');
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo eliminar.');
    }
  };

  const toggleActive = async (row) => {
    try {
      const { error } = await supabase.from('billing_entities').update({ is_active: !row.is_active }).eq('id', row.id);
      if (error) throw error;
      toast.success(row.is_active ? 'Emisor desactivado.' : 'Emisor activado.');
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo actualizar el estado.');
    }
  };

  if (loading) {
    return (
      <div className="w-full min-h-[calc(100vh-24px)] bg-[#F8FAFC] p-4 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <RefreshCcw className="animate-spin" size={18} />
          <div className="text-[10px] font-black uppercase tracking-widest">Cargando configuración...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[calc(100vh-24px)] bg-[#F8FAFC] p-2 md:p-4 font-sans text-slate-700">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center">
            <ShieldCheck className="text-[#51A7FD]" size={18} />
          </div>
          <div>
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-800">
              Configuración de Facturación
            </div>
            <div className="text-[9px] font-bold text-slate-400">
              Por emisor (Multi-CUIT) • Fiscal + Modo + Certificados • Sin configuración general
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={back}
            className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-[#51A7FD] shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
            title="Volver"
          >
            <ArrowLeft size={14} /> Volver
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <Activity size={14} className="text-[#51A7FD]" />
            Emisores
          </div>
          <div className="text-2xl font-black text-slate-900 mt-2">{billingEntities.length}</div>
          <div className="text-[9px] font-bold text-slate-400">CUITs configurados</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <Target size={14} className="text-emerald-500" />
            Activos
          </div>
          <div className="text-2xl font-black text-slate-900 mt-2">{activeCount}</div>
          <div className="text-[9px] font-bold text-slate-400">Disponibles para facturar</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <KeyRound size={14} className="text-slate-600" />
            Secretos
          </div>
          <div className="text-xl font-black text-slate-900 mt-2">{credsAvailable ? 'OK' : 'Pendiente'}</div>
          <div className="text-[9px] font-bold text-slate-400">
            {credsAvailable ? 'Tabla credenciales disponible' : 'Crear billing_entity_credentials'}
          </div>
        </div>

        {/* Removed Smart Pop-Out Card */}
      </div>

      {!credsAvailable && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl shadow-sm mt-3 p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-800">Atención</div>
          <div className="text-[10px] text-amber-800 mt-2">
            No pude leer/guardar credenciales por emisor. Posibles causas:
            <ul className="list-disc ml-5 mt-2">
              <li>No existe la tabla <b>billing_entity_credentials</b></li>
              <li>RLS/permisos impiden el acceso desde el cliente</li>
            </ul>
            Recomendación: creá la tabla (SQL que te pasé) y después definimos RLS o lo movemos 100% a Edge.
          </div>
        </div>
      )}

      {!certificateWizardOpen && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mt-3 px-4 py-3 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-800">Emisores (Multi-CUIT)</div>
            <div className="text-[9px] font-bold text-slate-400">
              {filtered.length} de {billingEntities.length}
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <div className="flex items-center gap-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full md:w-80 px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
                placeholder="Buscar por razón social, CUIT, fiscal..."
              />
              <button
                onClick={() => setOnlyActive((v) => !v)}
                className={`px-3 py-2 rounded-xl border shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2 ${
                  onlyActive
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:text-[#51A7FD]'
                }`}
                title="Filtrar activos"
              >
                <Target size={14} />
                {onlyActive ? 'Activos' : 'Todos'}
              </button>
            </div>

            <button
              data-onboarding-id="afip-emisor-nuevo"
              onClick={startNew}
              className="px-3 py-2 bg-[#51A7FD] text-white rounded-xl shadow-sm hover:opacity-90 transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
              title="Nuevo emisor"
            >
              <Plus size={14} />
              Nuevo
            </button>
          </div>
        </div>
      )}

      {editingId && beDraft && certificateWizardOpen && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mt-3 p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-800">
                Asistente de certificados ARCA / AFIP
              </div>
              <div className="text-[9px] font-bold text-slate-400 mt-1">
                Pantalla dedicada para generar CSR, descargar scripts y volver luego con el CRT y la KEY del emisor.
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCertificateWizardOpen(false)}
                className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-600 hover:text-slate-900 shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
                title="Volver al editor"
              >
                <ArrowLeft size={14} />
                Volver al editor
              </button>

              <button
                onClick={saveDraft}
                className="px-3 py-2 bg-[#0B2A36] text-white rounded-xl shadow-sm hover:opacity-90 transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
                title="Guardar emisor"
              >
                <Save size={14} />
                Guardar
              </button>
            </div>
          </div>

          <AfipCertificateWizard
            billingEntity={beDraft}
            organizationName={org?.name || ''}
            fiscalMeta={getFiscalMeta(beDraft)}
            onUpdateFiscalMeta={updateFiscalMeta}
          />
        </div>
      )}

      {/* Editor inline */}
      {editingId && beDraft && !certificateWizardOpen && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mt-3 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-800">
                {editingId === '__new__' ? 'Nuevo emisor' : 'Editar emisor'}
              </div>
              <div className="text-[9px] font-bold text-slate-400">
                Se factura desde este CUIT + certificados según el modo elegido en el emisor
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCertificateWizardOpen(true)}
                className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-600 hover:text-[#51A7FD] shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
                title="Abrir asistente de certificados"
              >
                <ShieldCheck size={14} />
                Asistente AFIP
              </button>

              <button
                onClick={resetEditor}
                className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-600 hover:text-slate-900 shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
                title="Cancelar"
              >
                <X size={14} />
                Cancelar
              </button>

              <button
                onClick={saveDraft}
                className="px-3 py-2 bg-[#0B2A36] text-white rounded-xl shadow-sm hover:opacity-90 transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
                title="Guardar emisor"
              >
                <Save size={14} />
                Guardar
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              onClick={() => jumpToSection('general')}
              className={`px-3 py-2 rounded-xl border shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2 ${
                focusSection === 'general'
                  ? 'bg-[#0B2A36] border-[#0B2A36] text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:text-[#51A7FD]'
              }`}
              title="Ir a datos del emisor"
            >
              <Pencil size={14} />
              Editar
            </button>

            <button
              onClick={() => jumpToSection('certificates')}
              className={`px-3 py-2 rounded-xl border shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2 ${
                focusSection === 'certificates'
                  ? 'bg-[#0B2A36] border-[#0B2A36] text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:text-[#51A7FD]'
              }`}
              title="Ir a certificados"
            >
              <KeyRound size={14} />
              Certificados
            </button>

            <button
              onClick={() => jumpToSection('limits')}
              className={`px-3 py-2 rounded-xl border shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2 ${
                focusSection === 'limits'
                  ? 'bg-[#0B2A36] border-[#0B2A36] text-white'
                  : 'bg-white border-slate-200 text-slate-600 hover:text-[#51A7FD]'
              }`}
              title="Ir a límites por mes"
            >
              <Hash size={14} />
              Límites
            </button>
          </div>

          {/* Datos principales */}
          <div ref={generalSectionRef} className="mt-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-800 mb-3">
              Datos del emisor
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 md:col-span-2">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Razón social</div>
              <input
                value={beDraft.tax_name || ''}
                onChange={(e) => updateDraft('tax_name', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
                placeholder="Mi Empresa S.A."
              />
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">CUIT</div>
              <input
                data-onboarding-id="afip-wizard-cuit"
                value={beDraft.cuit || ''}
                onChange={(e) => updateDraft('cuit', normalizeCuit(e.target.value))}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
                placeholder="20123456789"
              />
              <div className="text-[9px] font-bold text-slate-400 mt-2">11 dígitos, sin guiones</div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Tipo de responsable</div>
              <select
                value={beDraft.fiscal_configuration || 'Responsable Inscripto'}
                onChange={(e) => {
                  // Unificamos en ambos campos para no tener doble verdad
                  updateDraft('fiscal_configuration', e.target.value);
                  updateDraft('tax_condition', e.target.value);
                }}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
              >
                {FISCAL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div className="text-[9px] font-bold text-slate-400 mt-2">Se usa para tipo de comprobante</div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Punto de venta</div>
              <input
                data-onboarding-id="afip-wizard-pos"
                value={beDraft.afip_pto_venta ?? 1}
                onChange={(e) => updateDraft('afip_pto_venta', e.target.value)}
                type="number"
                min="1"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
                placeholder="1"
              />
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 md:col-span-2">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Domicilio comercial</div>
              <input
                value={beDraft.domicilio_comercial || ''}
                onChange={(e) => updateDraft('domicilio_comercial', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
                placeholder="Junín 385, Rosario"
              />
              <div className="text-[9px] font-bold text-slate-400 mt-2">Se imprime en el comprobante. Vacío = dirección de la empresa</div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Ingresos Brutos</div>
              <input
                value={beDraft.iibb || ''}
                onChange={(e) => updateDraft('iibb', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
                placeholder="921-7610000 / Conv. Multilateral"
              />
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Inicio de actividades</div>
              <input
                type="date"
                value={beDraft.inicio_actividades || ''}
                onChange={(e) => updateDraft('inicio_actividades', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
              />
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Modo del emisor</div>
              <select
                value={beDraft.afip_mode || 'homologacion'}
                onChange={(e) => updateDraft('afip_mode', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
              >
                {AFIP_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <div className="text-[9px] font-bold text-slate-400 mt-2">
                Usa CRT/KEY según este modo (HOMO/PROD)
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2">Estado</div>
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold text-slate-700">{beDraft.is_active ? 'Activo' : 'Inactivo'}</div>
                <button
                  onClick={() => updateDraft('is_active', !beDraft.is_active)}
                  className={`w-12 h-7 rounded-full relative transition-all border ${
                    beDraft.is_active ? 'bg-emerald-500 border-emerald-400' : 'bg-slate-200 border-slate-200'
                  }`}
                  title="Activar / Desactivar"
                >
                  <span
                    className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${
                      beDraft.is_active ? 'left-5' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>
              <div className="text-[9px] font-bold text-slate-400 mt-2">Inactivo: no se usa para facturar</div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2">Auto Facturación</div>
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold text-slate-700">{beDraft.auto_invoice ? 'Asignar' : 'No Asignar'}</div>
                <button
                  onClick={() => updateDraft('auto_invoice', !beDraft.auto_invoice)}
                  className={`w-12 h-7 rounded-full relative transition-all border ${
                    beDraft.auto_invoice ? 'bg-[#51A7FD] border-blue-400' : 'bg-slate-200 border-slate-200'
                  }`}
                  title="Activar / Desactivar Auto Facturación"
                >
                  <span
                    className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${
                      beDraft.auto_invoice ? 'left-5' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>
              <div className="text-[9px] font-bold text-slate-400 mt-2">Usado por motor backend</div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Prioridad</div>
              <input
                value={beDraft.priority ?? 1}
                onChange={(e) => updateDraft('priority', e.target.value)}
                type="number"
                min="1"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
                placeholder="1"
              />
              <div className="text-[9px] font-bold text-slate-400 mt-2">Orden de selección (si auto/por defecto)</div>
            </div>
          </div>

          {/* Credenciales */}
          <div ref={certificatesSectionRef} className="mt-4 bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-800 flex items-center gap-2">
                  <KeyRound size={14} className="text-slate-700" /> Certificados del emisor (por ambiente)
                </div>
                <div className="text-[9px] font-bold text-slate-400 mt-1">
                  Este CUIT factura con <b>sus</b> claves. En homologación usa CRT/KEY Homo; en producción usa CRT/KEY Prod.
                </div>
                {!credsAvailable && (
                  <div className="text-[9px] font-bold text-amber-700 mt-2">
                    No se puede persistir credenciales aún (tabla/permisos). Podés igual cargarlas para copiar/pegar luego.
                  </div>
                )}
              </div>

              {hasAnySecret && (
                <div className="text-[9px] font-bold text-slate-500">
                  Tip: guardá esto solo si estás cómodo con el modelo de seguridad actual.
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              {[
                { k: 'cert_homo', label: 'CRT Homologación (cert_homo)' },
                { k: 'key_homo', label: 'KEY Homologación (key_homo)' },
                { k: 'cert_prod', label: 'CRT Producción (cert_prod)' },
                { k: 'key_prod', label: 'KEY Producción (key_prod)' }
              ].map((f) => (
                <div key={f.k} className="bg-white border border-slate-200 rounded-2xl p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{f.label}</div>
                    <div className="flex items-center gap-2">
                      <label
                        className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 hover:text-[#51A7FD] shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2 cursor-pointer"
                        title="Cargar archivo .crt/.key/.pem"
                      >
                        <Upload size={14} />
                        Cargar
                        <input
                          type="file"
                          accept=".crt,.key,.pem,.txt,.cer"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            // permitir volver a cargar el mismo archivo
                            e.target.value = '';
                            await handleSecretFile(f.k, file);
                          }}
                        />
                      </label>

                      <button
                        onClick={() => toggleShowSecret(f.k)}
                        className="p-2 rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-slate-900 shadow-sm transition-all"
                        title={showSecrets[f.k] ? 'Ocultar' : 'Mostrar'}
                      >
                        {showSecrets[f.k] ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>

                      <button
                        onClick={() => clearSecret(f.k)}
                        className="p-2 rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-rose-600 shadow-sm transition-all"
                        title="Limpiar"
                      >
                        <Eraser size={14} />
                      </button>
                    </div>
                  </div>

                  {secretFiles[f.k] ? (
                    <div className="mt-2 text-[9px] font-bold text-slate-400">
                      Archivo: <span className="text-slate-600">{secretFiles[f.k]}</span>
                    </div>
                  ) : null}

                  <textarea
                    data-onboarding-id="afip-wizard-cert"
                    value={secretDraft[f.k] || ''}
                    onChange={(e) => updateSecret(f.k, e.target.value)}
                    className="mt-2 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold min-h-[110px]"
                    placeholder="Pegá acá el contenido del archivo .crt / .key (PEM) o cargalo con el botón"
                    style={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                    }}
                  />

                  {!showSecrets[f.k] && (secretDraft[f.k] || '').length > 0 && (
                    <div className="mt-2 text-[9px] font-bold text-slate-400">
                      Valor cargado: {String(secretDraft[f.k] || '').length.toLocaleString('es-AR')} chars
                    </div>
                  )}

                  {(secretDraft[f.k] || '').trim() && (
                    <div className="mt-2 text-[9px] font-bold text-slate-400">
                      {String(secretDraft[f.k] || '').includes('BEGIN') && String(secretDraft[f.k] || '').includes('END')
                        ? 'Formato PEM detectado'
                        : 'Contenido cargado (sin cabecera PEM)'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Límites mensuales */}
          <div ref={limitsSectionRef} className="mt-4 bg-slate-50 border border-slate-200 rounded-2xl p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-800 flex items-center gap-2">
                  <Hash size={14} className="text-slate-600" /> Límites por mes (monthly_limits)
                </div>
                <div className="text-[9px] font-bold text-slate-400">Control de tope mensual por emisor • clave YYYY-MM</div>
              </div>

              <div className="flex flex-col md:flex-row gap-2 md:items-center">
                <input
                  value={limitMonth}
                  onChange={(e) => setLimitMonth(e.target.value)}
                  className="w-full md:w-32 px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
                  placeholder="YYYY-MM"
                />
                <input
                  value={limitAmount}
                  onChange={(e) => setLimitAmount(e.target.value)}
                  className="w-full md:w-40 px-3 py-2 rounded-xl border border-slate-200 bg-white text-[12px] font-bold"
                  placeholder="Monto (ej: 150000)"
                />
                <button
                  onClick={setMonthlyLimit}
                  className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-600 hover:text-emerald-700 hover:border-emerald-200 shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
                  title="Agregar/Actualizar límite"
                >
                  <Check size={14} />
                  Aplicar
                </button>
              </div>
            </div>

            {monthlyLimitsPairs.length === 0 ? (
              <div className="mt-3 text-[10px] text-slate-500">No hay límites configurados. Podés empezar por el mes actual.</div>
            ) : (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                {monthlyLimitsPairs.map(([mk, amt]) => (
                  <div key={mk} className="bg-white border border-slate-200 rounded-2xl p-3 flex items-center justify-between">
                    <div>
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{mk}</div>
                      <div className="text-[12px] font-black text-slate-900 mt-1">
                        ${Number(amt || 0).toLocaleString('es-AR')}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteMonthlyLimit(mk)}
                      className="p-2 rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-rose-600 shadow-sm transition-all"
                      title="Eliminar límite"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 text-[9px] font-bold text-slate-400">
              Importante: los cambios en límites quedan en borrador hasta que guardes el emisor.
            </div>
          </div>
        </div>
      )}

      {!certificateWizardOpen && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mt-3 overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  <th className="px-4 py-3">Emisor</th>
                  <th className="px-4 py-3">CUIT</th>
                  <th className="px-4 py-3">Responsable</th>
                  <th className="px-4 py-3">Pto Vta</th>
                  <th className="px-4 py-3">Modo</th>
                  <th className="px-4 py-3">Límite mes</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-[10px] text-slate-500" colSpan={8}>
                      No hay emisores para mostrar.
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => {
                    const fiscal = row.fiscal_configuration || row.tax_condition || '—';
                    const modeLabel = String(row.afip_mode || 'homologacion') === 'produccion' ? 'Producción' : 'Homologación';

                    const currentMonthKey = buildMonthKeyNow();
                    const currentMonthLimit = getRowMonthLimit(row, currentMonthKey);
                    const limitsCount = getRowLimitsCount(row);
                    const creds = credsByBE?.[row.id];
                    const hasCreds = Boolean(
                      (creds?.cert_homo || '').trim() ||
                        (creds?.key_homo || '').trim() ||
                        (creds?.cert_prod || '').trim() ||
                        (creds?.key_prod || '').trim()
                    );

                    return (
                      <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50 transition-all">
                        <td className="px-4 py-3">
                          <div className="text-[12px] font-black text-slate-900">{row.tax_name}</div>
                          <div className="text-[9px] font-bold text-slate-400">
                            Prioridad: {row.priority ?? 1} • Credenciales: {credsAvailable ? (hasCreds ? 'Cargadas' : '—') : 'Pendiente'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[11px] font-bold text-slate-700">{row.cuit}</td>
                        <td className="px-4 py-3">
                          <div className="text-[11px] font-black text-slate-700">{fiscal}</div>
                        </td>
                        <td className="px-4 py-3 text-[11px] font-bold text-slate-700">{row.afip_pto_venta ?? 1}</td>
                        <td className="px-4 py-3 text-[11px] font-bold text-slate-700">{modeLabel}</td>
                        <td className="px-4 py-3">
                          <div className="text-[11px] font-black text-slate-700">
                            {currentMonthLimit > 0 ? `$${currentMonthLimit.toLocaleString('es-AR')}` : 'Sin tope'}
                          </div>
                          <div className="text-[9px] font-bold text-slate-400">
                            {currentMonthKey} • {limitsCount} mes{limitsCount === 1 ? '' : 'es'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleActive(row)}
                            className={`px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-widest shadow-sm transition-all ${
                              row.is_active
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                                : 'bg-slate-50 border-slate-200 text-slate-500 hover:text-slate-700'
                            }`}
                            title="Cambiar estado"
                          >
                            {row.is_active ? 'Activo' : 'Inactivo'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                            <button
                              onClick={() => startEdit(row, 'general')}
                              className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-600 hover:text-[#51A7FD] shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
                              title="Editar"
                            >
                              <Pencil size={14} />
                              Editar
                            </button>

                            <button
                              onClick={() => startEdit(row, 'certificates')}
                              className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-600 hover:text-[#51A7FD] shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
                              title="Abrir certificados"
                            >
                              <KeyRound size={14} />
                              Certificados
                            </button>

                            <button
                              onClick={() => startEdit(row, 'limits')}
                              className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-600 hover:text-emerald-700 hover:border-emerald-200 shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
                              title="Abrir límites"
                            >
                              <Hash size={14} />
                              Límites
                            </button>

                            <button
                              onClick={() => removeEntity(row.id)}
                              className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-600 hover:text-rose-600 shadow-sm transition-all text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-2"
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!certificateWizardOpen && (
        <div className="mt-3 text-[9px] font-bold text-slate-400">
          Nota estratégica: el “modo” es por emisor. Así podés tener un CUIT en prod y otro en homo sin mezclar certificados.
        </div>
      )}
    </div>
  );
}
